<template>
  <div class="main-data" v-loading="loading">
    <div class="page-header">
      <div class="page-header__title">
        <span>Loyihalar ijrosi</span>
        <el-tag v-if="boardTitle" size="large" type="warning">
          {{ boardTitle }} {{ boardYear }}
        </el-tag>
      </div>
      <div class="page-header__actions">
        <el-dropdown
          trigger="click"
          placement="bottom-end"
          :hide-on-click="false"
          @command="onReportCommand"
        >
          <el-button type="success" :loading="anyDownloading">
            <el-icon class="report-btn__icon"><Download /></el-icon>
            Hisobotlar (Excel)
            <el-icon class="el-icon--right"><ArrowDown /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu class="report-menu">
              <el-dropdown-item
                v-for="report in reports"
                :key="report.kind"
                :command="report.kind"
                :disabled="downloading[report.kind]"
              >
                <div class="report-item">
                  <div class="report-item__icon">
                    <el-icon v-if="downloading[report.kind]" class="is-loading"><Loading /></el-icon>
                    <el-icon v-else><component :is="report.icon" /></el-icon>
                  </div>
                  <div class="report-item__text">
                    <span class="report-item__title">{{ report.title }}</span>
                    <span class="report-item__hint">{{ report.hint }}</span>
                  </div>
                  <span class="report-item__excel">
                    <el-icon><Download /></el-icon>
                    <span v-if="downloading[report.kind]">Yuklanmoqda...</span>
                    <span v-else>Excel</span>
                  </span>
                </div>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <el-button type="info" @click="goBack">Orqaga</el-button>
      </div>
    </div>

    <div class="toolbar">
      <el-input
        v-model="search"
        placeholder="Loyiha nomi, ID yoki tavsifi bo'yicha qidirish"
        clearable
        size="large"
        class="toolbar__search"
        @input="onSearch"
        @clear="onSearch"
      />
      <el-select
        v-model="deputyId"
        class="toolbar__filter"
        size="large"
        clearable
        filterable
        placeholder="Deputat bo'yicha"
        :loading="filtersLoading"
        @change="onFilterChange"
      >
        <el-option
          v-for="deputy in deputyOptions"
          :key="deputy.deputy_id"
          :value="deputy.deputy_id"
          :label="deputy.fullname"
        />
      </el-select>
      <el-select
        v-model="okrugId"
        class="toolbar__filter"
        size="large"
        clearable
        filterable
        placeholder="Okrug bo'yicha"
        :loading="filtersLoading"
        @change="onFilterChange"
      >
        <el-option
          v-for="okrug in okrugOptions"
          :key="okrug.okrug_id"
          :value="okrug.okrug_id"
          :label="okrug.okrug_title"
        />
      </el-select>
    </div>

    <div v-if="initiatives.length" class="feed">
      <div
        v-for="item in initiatives"
        :key="item.choice_id"
        class="feed-card"
      >
        <div class="feed-card__media">
          <img
            v-if="firstImage(item)"
            :src="firstImage(item)"
            alt=""
            class="feed-card__img"
            @error="onImgError"
          />
          <div v-else class="feed-card__img feed-card__img--empty">
            Rasm yo'q
          </div>
          <div v-if="imageCount(item) > 1" class="feed-card__img-count">
            +{{ imageCount(item) - 1 }}
          </div>
        </div>

        <div class="feed-card__body">
          <div class="feed-card__top">
            <div class="feed-card__title">{{ item.title || "-" }}</div>
            <span class="feed-card__chosen">
              {{ item.chosen_at ? $moment(item.chosen_at).format("DD-MM-YYYY / HH:mm") : "-" }}
            </span>
          </div>

          <div class="feed-card__badges">
            <el-tag size="small" type="success" effect="plain">
              {{ stageLabel(item.stage) }}
            </el-tag>
            <el-tag v-if="item.type" size="small" type="info" effect="plain">
              {{ item.type }}
            </el-tag>
            <el-tag v-if="item.category_title" size="small" effect="plain">
              {{ item.category_title }}
            </el-tag>
            <span v-if="item.public_id" class="feed-card__public-id">
              ID: {{ item.public_id }}
            </span>
          </div>

          <p v-if="item.description" class="feed-card__desc">
            {{ item.description }}
          </p>

          <div class="feed-card__grid">
            <div class="feed-card__field">
              <span class="feed-card__label">Deputat</span>
              <span class="feed-card__value">
                {{ item.deputy_fullname || "-" }}
                <span v-if="item.deputy_pinfl" class="feed-card__muted">
                  ({{ item.deputy_pinfl }})
                </span>
              </span>
            </div>
            <div class="feed-card__field">
              <span class="feed-card__label">Okrug</span>
              <span class="feed-card__value">{{ item.okrug_title || "—" }}</span>
            </div>
            <div class="feed-card__field">
              <span class="feed-card__label">Hudud</span>
              <span class="feed-card__value">{{ locationText(item) }}</span>
            </div>
            <div class="feed-card__field">
              <span class="feed-card__label">Ajratilgan</span>
              <span class="feed-card__value">{{ money(item.granted_amount) }} so'm</span>
            </div>
            <div class="feed-card__field">
              <span class="feed-card__label">So'ralgan</span>
              <span class="feed-card__value">{{ money(item.requested_amount) }} so'm</span>
            </div>
            <div class="feed-card__field">
              <span class="feed-card__label">Ovozlar</span>
              <span class="feed-card__value">{{ item.vote_count ?? 0 }}</span>
            </div>
            <div v-if="item.author_fullname" class="feed-card__field">
              <span class="feed-card__label">Tashabbuskor</span>
              <span class="feed-card__value">{{ item.author_fullname }}</span>
            </div>
          </div>

          <div class="feed-card__footer">
            <el-link
              v-if="item.initiative_link"
              :href="item.initiative_link"
              target="_blank"
              rel="noopener"
              type="primary"
            >
              <el-button type="primary" size="default">Loyihani ochish</el-button>
            </el-link>
          </div>
        </div>
      </div>
    </div>

    <el-empty
      v-else-if="!loading"
      description="Hali g'olib loyihalar tanlanmagan."
    />

    <div v-if="totalCount" class="pagination">
      <div>Loyihalar soni: {{ totalCount }}</div>
      <el-pagination
        background
        layout="sizes, prev, pager, next"
        :total="totalCount"
        v-model:page-size="limit"
        v-model:current-page="currentPage"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
import { ElNotification } from "element-plus";
import {
  Document,
  Clock,
  Money,
  ArrowDown,
  Download,
  Loading,
} from "@element-plus/icons-vue";
import { formatterPrice, blobErrorMessage } from "@/utils/common";

export default {
  name: "BoardChosenInitiatives",
  components: { Document, Clock, Money, ArrowDown, Download, Loading },
  data() {
    return {
      loading: false,
      boardTitle: this.$route.query.title || "",
      boardYear: this.$route.query.year || "",
      initiatives: [],
      totalCount: 0,
      limit: 20,
      currentPage: 1,
      search: "",
      searchTimer: null,
      deputyId: null,
      okrugId: null,
      // per-report Excel download spinners (§3.2.c.1 — 3 plain GET downloads)
      downloading: {
        "chosen-initiatives": false,
        "chron-statistics": false,
        financial: false,
      },
      // "Hisobotlar" dropdown items
      reports: [
        {
          kind: "chosen-initiatives",
          title: "Loyihalar ijrosi",
          hint: "Tanlangan g'olib loyihalar (filtrlar bilan)",
          fileName: "loyihalar-ijrosi.xlsx",
          icon: "Document",
        },
        {
          kind: "chron-statistics",
          title: "Xronologiya",
          hint: "Xronologiya statistikasi",
          fileName: "xronologiya.xlsx",
          icon: "Clock",
        },
        {
          kind: "financial",
          title: "Moliyaviy",
          hint: "Moliyaviy statistika",
          fileName: "moliyaviy.xlsx",
          icon: "Money",
        },
      ],
      // filter option sources (from the board okrug grid)
      filtersLoading: false,
      okrugOptions: [],
      deputyOptions: [],
      fileBaseUrl: `${process.env.VUE_APP_BASE_API_URL}api/v2/info/file/`,
    };
  },
  computed: {
    boardId() {
      return Number(this.$route.params.boardId);
    },
    anyDownloading() {
      return Object.values(this.downloading).some(Boolean);
    },
  },
  methods: {
    money(value) {
      return formatterPrice(value || 0);
    },
    stageLabel(stage) {
      const map = {
        WINNER: "G'olib",
        PASSED: "O'tgan",
      };
      return map[stage] || stage || "-";
    },
    locationText(item) {
      return [item.region_title, item.district_title, item.quarter_title]
        .filter(Boolean)
        .join(", ") || "—";
    },
    imageCount(item) {
      return Array.isArray(item.images) ? item.images.length : 0;
    },
    firstImage(item) {
      if (!Array.isArray(item.images) || !item.images.length) return null;
      return this.fileBaseUrl + item.images[0];
    },
    onImgError(event) {
      event.target.style.visibility = "hidden";
    },
    loadFilters() {
      this.filtersLoading = true;
      this.$http
        .get(`api/v1/deputyv2/deputy-manager/boards/${this.boardId}/okrugs`, {
          params: { limit: 200, offset: 0 },
        })
        .then(({ data }) => {
          const okrugs = data.okrugs || [];
          this.okrugOptions = okrugs.map((okrug) => ({
            okrug_id: okrug.okrug_id,
            okrug_title: okrug.okrug_title,
          }));
          const seen = new Set();
          const deputies = [];
          okrugs.forEach((okrug) => {
            (okrug.deputies || []).forEach((deputy) => {
              if (!seen.has(deputy.deputy_id)) {
                seen.add(deputy.deputy_id);
                deputies.push({
                  deputy_id: deputy.deputy_id,
                  fullname: deputy.fullname,
                });
              }
            });
          });
          this.deputyOptions = deputies;
        })
        .catch(() => {})
        .finally(() => {
          this.filtersLoading = false;
        });
    },
    getInitiatives() {
      this.loading = true;
      this.$http
        .get(`api/v1/deputyv2/deputy-manager/boards/${this.boardId}/chosen-initiatives`, {
          params: {
            search: this.search || null,
            deputy_id: this.deputyId || null,
            okrug_id: this.okrugId || null,
            limit: this.limit,
            offset: this.limit * (this.currentPage - 1),
            cache: "off",
          },
        })
        .then(({ data }) => {
          this.initiatives = data.initiatives || [];
          this.totalCount = data.count || 0;
        })
        .catch(({ response }) => {
          ElNotification({
            type: "error",
            title: "Xatolik",
            message: response?.data?.message || "Yuklashda xatolik sodir bo'ldi",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.getInitiatives();
      }, 400);
    },
    onFilterChange() {
      this.currentPage = 1;
      this.getInitiatives();
    },
    handleCurrentChange() {
      this.getInitiatives();
    },
    handleSizeChange() {
      this.currentPage = 1;
      this.getInitiatives();
    },
    onReportCommand(kind) {
      const report = this.reports.find((item) => item.kind === kind);
      if (!report || this.downloading[kind]) return;
      this.downloadExcel(kind, report.fileName);
    },
    getFileName(headers, fallback) {
      const contentDisposition =
        headers?.["content-disposition"] || headers?.["Content-Disposition"];
      if (!contentDisposition) return fallback;
      const part = contentDisposition
        .split(";")
        .find((item) => item.trim().startsWith("filename="));
      if (!part) return fallback;
      return decodeURIComponent(part.split("=")[1].trim().replace(/^"(.*)"$/, "$1"));
    },
    // §3.2.c.1 — three plain synchronous GET downloads. Only "chosen-initiatives"
    // respects the list filters; the other two are whole-board.
    downloadExcel(kind, fallbackName) {
      this.downloading[kind] = true;
      const report = this.reports.find((item) => item.kind === kind);
      const label = report?.title || "Hisobot";
      // In-progress state is shown by the button spinner + the item's
      // "Yuklanmoqda..." pill, so only a single completion toast is raised here.
      const params =
        kind === "chosen-initiatives"
          ? {
              search: this.search || null,
              deputy_id: this.deputyId || null,
              okrug_id: this.okrugId || null,
            }
          : {};
      this.$http
        .get(
          `api/v1/deputyv2/deputy-manager/boards/${this.boardId}/${kind}/excel`,
          { params, responseType: "blob" }
        )
        .then((res) => {
          const url = URL.createObjectURL(
            new Blob([res.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
            })
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", this.getFileName(res.headers, fallbackName));
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          ElNotification({
            type: "success",
            title: "Yuklab olindi",
            message: `"${label}" hisoboti yuklab olindi`,
          });
        })
        .catch(async ({ response }) => {
          ElNotification({
            type: "warning",
            title: "Ogohlantirish",
            message: await blobErrorMessage(
              response,
              "Excel yuklashda xatolik sodir bo'ldi!"
            ),
          });
        })
        .finally(() => {
          this.downloading[kind] = false;
        });
    },
    goBack() {
      // Reachable from several origins (Loyihalar ijrosi board picker, board
      // okrug grid) — return to wherever the user came from.
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push({ name: "ChosenInitiativesBoards" });
      }
    },
  },
  mounted() {
    this.loadFilters();
    this.getInitiatives();
  },
};
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.page-header__title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 22px;
  font-weight: 600;
  flex-wrap: wrap;
}
.page-header__actions {
  display: flex;
  gap: 10px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.toolbar__search {
  width: 360px;
  max-width: 100%;
}
.toolbar__filter {
  width: 240px;
}
.feed {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.feed-card {
  display: flex;
  gap: 16px;
  border: 1px solid #dcdfe6;
  border-radius: 12px;
  padding: 16px;
  background: #fff;
}
.feed-card__media {
  position: relative;
  flex-shrink: 0;
  width: 160px;
}
.feed-card__img {
  width: 160px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  background: #f5f7fa;
  display: block;
}
.feed-card__img--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 13px;
}
.feed-card__img-count {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
}
.feed-card__body {
  flex: 1;
  min-width: 0;
}
.feed-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.feed-card__title {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}
.feed-card__chosen {
  flex-shrink: 0;
  font-size: 13px;
  color: #909399;
  white-space: nowrap;
}
.feed-card__badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}
.feed-card__public-id {
  font-size: 12px;
  color: #909399;
  font-family: monospace;
}
.feed-card__desc {
  margin: 10px 0 0;
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.feed-card__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px 24px;
  margin-top: 12px;
}
.feed-card__field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.feed-card__label {
  font-size: 12px;
  color: #909399;
}
.feed-card__value {
  font-size: 14px;
  color: #303133;
}
.feed-card__muted {
  color: #909399;
  font-size: 13px;
}
.feed-card__footer {
  margin-top: 14px;
}
.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
}
.report-btn__icon {
  margin-right: 6px;
}
</style>

<style>
/* Dropdown is teleported to body — keep these rules global (not scoped). */
.report-menu {
  padding: 6px;
  min-width: 280px;
}
.report-menu .el-dropdown-menu__item {
  padding: 0;
  border-radius: 8px;
  margin-bottom: 2px;
}
.report-menu .el-dropdown-menu__item:last-child {
  margin-bottom: 0;
}
.report-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  width: 100%;
}
.report-item__icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f9eb;
  color: #67c23a;
  font-size: 19px;
}
/* Element Plus adds a default margin to .el-icon inside dropdown items —
   reset it so the glyph sits dead-center in the badge. */
.report-item__icon .el-icon {
  margin: 0;
  width: 1em;
  height: 1em;
}
.report-item__icon .is-loading {
  animation: report-spin 0.9s linear infinite;
}
@keyframes report-spin {
  to {
    transform: rotate(360deg);
  }
}
.report-item__text {
  display: flex;
  flex-direction: column;
  line-height: 1.35;
  flex: 1;
  min-width: 0;
}
.report-item__excel {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: #67c23a;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.report-item__excel .el-icon {
  margin: 0;
  font-size: 13px;
}
.report-item__title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.report-item__hint {
  font-size: 12px;
  color: #909399;
}
</style>
